export interface IApiOptions {
    includeAnnotations?: boolean;
    returnRecord?: boolean;
    version?: string;
    pageSize?: number;
    body?: any;
    additionalHeaders?: any;
    noAccept?: boolean;
}

export class ApiError extends Error {
    status: number = -1;
    code?: string;
    detail?: string;

    constructor(
        status: number,
        message: string,
        code?: string,
        detail?: string
    ) {
        super(message);
        this.status = status;
        this.code = code;
        this.detail = detail;
    }
}

export interface IXrmDataSource {
    /**
     * Returns the base URL for api calls in the form:
     * {url}/api/data/v{version}
     * ex.
     * https://test.crm.dynamics.com/api/data/v9.1
     */
    getBaseUrl(): string;

    /**
     * Executes an arbitrary request to D365 with the given headers
     * @param method the http method
     * @param url the url content of the request (accounts?$filter=name eq 'test'... for example)
     * @param headers any additional headers for the request
     * @param body the body of the request
     * @param pageSize the maximum number of records to return
     */
    raw(method: string, url: string, options?: IApiOptions): Promise<Response>;

    /**
     * Executes an arbitrary request to D365 with default headers and attempts to parse the response to JSON.
     * @param method the http method
     * @param url the url content of the request (accounts?$filter=name eq 'test'... for example)
     * @param headers any additional headers for the request
     * @param body the body of the request
     * @param pageSize the maximum number of records to return
     */
    execute(method: string, url: string, options?: IApiOptions): Promise<any>;

    /**
     * Executes a page request to D365 with default headers and attempts to parse the response to JSON.
     * @param method the http method
     * @param url the nextLink content from a previous request
     * @param headers any additional headers for the request
     * @param body the body of the request
     * @param pageSize the maximum number of records to return
     */
    page(method: string, url: string, options?: IApiOptions): Promise<any>;

    whoAmI(): Promise<any>;

    /**
     * Executes an OData query
     * @param query an OData query (ex. $select=name&$filter=name eq 'test'&$top=3&$expand=...)
     */
    query(query: string): Promise<any[]>;

    /**
     * Executes an OData query using batch to avoid the URL length limit
     * @param query  an OData query (ex. $select=name&$filter=name eq 'test'&$top=3&$expand=...)
     */
    queryBatch(query: string): Promise<any[]>;

    /**
     * Executes an OData query and performs paging
     * @param query an OData query (ex. $select=name&$filter=name eq 'test'&$top=3&$expand=...)
     */
    queryAll(query: string, pageCount: number): Promise<any[]>;

    /**
     * Executes a Fetch XML query
     * @param query a fetch XML
     */
    fetch(collectionName: string, query: string): Promise<any[]>;

    /**
     * Executes a query based on a view id.
     * @param viewId the view id
     */
    view(viewId: string): Promise<any[]>;

    /**
     * Gets the record with the given id.
     * The id can be either a GUID or an alternate key as defined here:
     * https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/retrieve-entity-using-web-api#retrieve-using-an-alternate-key
     * @param id the id of the record
     * @param fields a comma separated string of field names (ex. name,accountid,...)
     */
    getRecord(collectionName: string, id: string, fields: string): Promise<any>;

    /**
     * Gets the value for a specified field on the given record.
     * @param id the id of the record
     * @param field the field name
     */
    getRecordProperty<E>(collectionName: string, id: string, field: string): Promise<E>;

    /**
     * Gets detailed information about a lookup field on the given record.
     * @param id the id of the record
     * @param field the field name
     * @param lookupFields the fields on the lookup entity to retrieve
     */
    getRecordLookupProperty<E>(collectionName: string, id: string, field: string, lookupFields: string): Promise<E>;

    /**
     * Creates a new record
     * NOTE: for setting lookup values, see here:
     * https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/associate-disassociate-entities-using-web-api#associate-entities-on-update-using-single-valued-navigation-property
     * @param data
     */
    create(collectionName: string, data: any): Promise<void>;

    /**
     * Creates a new record and returns the fields specified of that record
     * NOTE: for setting lookup values, see here:
     * https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/associate-disassociate-entities-using-web-api#associate-entities-on-update-using-single-valued-navigation-property
     * @param data
     * @param fields a comma separated string of field names (ex. name,accountid,...)
     */
    createAndReturn(collectionName: string, data: any, fields?: string[]): Promise<any>;

    /**
     * Updates an existing record.
     * NOTE: will also do an upsert if the given id doesn't exist
     * NOTE: for setting lookup values, see here:
     * https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/associate-disassociate-entities-using-web-api#associate-entities-on-update-using-single-valued-navigation-property
     * @param id the record id
     * @param data
     */
    update(collectionName: string, id: string, data: any, version?: number): Promise<void>;

    /**
     * Updates an existing record and returns the fields specified of that record
     * NOTE: will also do an upsert if the given id doesn't exist
     * NOTE: for setting lookup values, see here:
     * https://docs.microsoft.com/en-us/powerapps/developer/common-data-service/webapi/associate-disassociate-entities-using-web-api#associate-entities-on-update-using-single-valued-navigation-property
     * @param id the record id
     * @param data
     * @param fields a comma separated string of field names (ex. name,accountid,...)
     */
    updateAndReturn(collectionName: string, id: string, data: any, fields?: string[], version?: number): Promise<any>;

    /**
     * Updates a single field on the given record
     * @param id the record id
     * @param property the property name
     * @param value the new value
     */
    updateField(collectionName: string, id: string, property: string, value: any, version?: number): Promise<void>;

    /**
     * Clears a single field on the given record
     * NOTE: primarily for clearing lookup values since you can't from a normal update request
     * @param id the record id
     * @param property the property name
     */
    clearField(collectionName: string, id: string, property: string, version?: number): Promise<void>;

    /**
     * Associates the given record with the other record
     * @param id the record id
     * @param relationship the relationship name
     * @param other the id and collection name for the other record
     */
    associate(collectionName: string, id: string, relationship: string, other: { id: string, collectionName: string }): Promise<void>;

    /**
     * Disassociates the given record with the other record
     * @param id the record id
     * @param relationship the relationship name
     * @param otherId the other record's id
     */
    disassociate(collectionName: string, id: string, relationship: string, otherId: string): Promise<void>;

    /**
     * Deletes the given record
     * @param id
     */
    delete(collectionName: string, id: string): Promise<void>;

    /**
     * Returns entity metadata for a given entity
     * @param logicalName the logical name of the entity
     * @param query the query string, for example: '?$select=LogicalCollectionName,PrimaryNameAttribute,PrimaryIdAttribute'
     */
    getEntityMetadata(logicalName: string, query: string): Promise<any>;
}